import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { LogoSpinner } from './LogoSpinner';

/**
 * PublicRoute - Rutas SOLO para usuarios NO autenticados
 * Ejemplos: /login, /register
 * - Redirige a /home si el usuario YA está autenticado
 * - NO renderiza Layout (páginas sin header/footer)
 */
export const PublicRoute = ({ component: Component, ...rest }) => {
    const { isAuthenticated, isAuthReady } = useAuthStore();

    // Mostrar spinner mientras se verifica la autenticación
    if (!isAuthReady) {
        return <LogoSpinner />;
    }

    return (
        <Route
            {...rest}
            render={(props) =>
                !isAuthenticated() ? (
                    <Component {...props} />
                ) : (
                    <Redirect to="/home" />
                )
            }
        />
    );
};

/**
 * ProtectedRoute - Rutas SOLO para usuarios autenticados
 * Ejemplos: /profile, /company-profile
 * - Redirige a /login si el usuario NO está autenticado
 * - Si requiresApproval=true y es empresa PENDIENTE, redirige a /company-profile
 * - SÍ renderiza dentro de Layout (con header/footer)
 * NOTA: El Layout se maneja en App.js, no aquí
 */
export const ProtectedRoute = ({ component: Component, requiresApproval = false, ...rest }) => {
    const { isAuthenticated, auth, isAuthReady } = useAuthStore();

    // Mostrar spinner mientras se verifica la autenticación
    if (!isAuthReady) {
        return <LogoSpinner />;
    }

    return (
        <Route
            {...rest}
            render={(props) => {
                if (!isAuthenticated()) {
                    return <Redirect to="/login" />;
                }

                // Verificar si es empresa y requiere aprobación
                const isCompany = auth.user?.tipoPerfil === 'EMPRESA';
                const isPending = auth.user?.estado === 'PENDIENTE';

                if (requiresApproval && isCompany && isPending) {
                    return <Redirect to="/company-profile" />;
                }

                return <Component {...props} />;
            }}
        />
    );
};

/**
 * PublicPageRoute - Rutas públicas accesibles para TODOS
 * Ejemplos: /, /home, /about-us
 * - Accesible tanto para usuarios autenticados como no autenticados
 * - SÍ renderiza dentro de Layout (con header/footer)
 * NOTA: El Layout se maneja en App.js, no aquí
 */
export const PublicPageRoute = ({ component: Component, ...rest }) => {
    return (
        <Route
            {...rest}
            render={(props) => <Component {...props} />}
        />
    );
};
